/* eslint-disable no-unused-vars */
import { useRef, useState } from 'react'
import { Button, Col, Container, Form, Row, Table, Card } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { transformKey } from '../../helpers/constants'
import { Trash2, FileText } from 'lucide-react'
import api from '../../config/AxiosInstance'
import ModalSeries from '../../components/modal/Series/Modal'
import toast, { Toaster } from 'react-hot-toast'
const FILE_SIZE = 5000 * 1024 // 5MB in bytes

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

function CreateMaterialIssuePage() {
  const { state } = useLocation()
  const navigate = useNavigate()
  const uploadFileAttachment = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [data, setData] = useState({
    items: state.items,
    attachments: [],
    series_number: null
  })
  const [showModal, setShowModal] = useState(false)

  const handleDragOver = (event) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (event) => {
    event.preventDefault()
    const file = event.dataTransfer.files[0]
    if (!file) {
      const errMsg = 'File attachment is required'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      setIsDragging(false)
      return
    }
    if (file.type !== 'application/pdf') {
      const errMsg = 'File attachment must be file PDF'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      setIsDragging(false)
      return
    }
    if (file.size > FILE_SIZE) {
      const errMsg = 'File attachment must be less than 5MB'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }
    setData((prevData) => ({
      ...prevData,
      attachments: [...prevData.attachments, file]
    }))
    event.dataTransfer.clearData()
  }

  const handleChangeUploadAttachment = (e) => {
    const file = e.target.files[0]
    if (!file) {
      const errMsg = 'File attachment is required'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }

    if (file.type !== 'application/pdf') {
      const errMsg = 'File attachment must be file PDF'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }

    if (file.size > FILE_SIZE) {
      const errMsg = 'File attachment must be less than 5MB'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
      return
    }
    setData((prevData) => ({
      ...prevData,
      attachments: [...prevData.attachments, file]
    }))
  }

  const handleModal = () => {
    setShowModal((prevShowModal) => !prevShowModal)
  }

  const handleSubmitTicket = async () => {
    try {
      setSubmitLoading(true)
      const payload = {
        items: data.items.map((item) => ({
          slot_item_id: item.slot_item_id,
          bu: item.bu
        })),
        attachment: data.attachments,
        warehouse_id: state.warehouse_id,
        series: data.series_number
      }
      const res = await api.postForm('material-issue/new-ticket', payload)
      const { status } = res
      if (status === 200) {
        handleModal()
        toast.success('Material Issue created!', {
          position: 'top-right',
          style: {
            fontSize: '14px',
            fontWeight: '600'
          }
        })
        navigate(-1)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleChangeDataInput = (type, value) => {
    setData((prevData) => ({
      ...prevData,
      [type]: value
    }))
  }

  const handleRemoveUploadAttachment = (index) => {
    setData((prevData) => ({
      ...prevData,
      attachments: prevData.attachments.filter((_, i) => i !== index)
    }))
  }

  const handleDelete = (data) => {
    setData((prevData) => ({
      ...prevData,
      items: prevData.items.filter(
        (item) => item.slot_item_id !== data.slot_item_id
      )
    }))
  }

  const handlePressBack = () => {
    navigate(-1)
  }

  const handleChangeInput = (slotItemId, value) => {
    setData((prevData) => ({
      ...prevData,
      items: prevData.items.map((item) => {
        if (item.slot_item_id === slotItemId) {
          return {
            ...item,
            bu: value
          }
        }
        return item
      })
    }))
  }

  const handleButtonSubmit = () => {
    if (data.items.length === 0) {
      return 'btn-secondary'
    }
    if (
      data.items.every((item) => !item.bu || item.bu === '') ||
      !data.attachments.length
    ) {
      return 'btn-secondary'
    }
    return 'btn-primary-orange'
  }

  const columns = [
    {
      key: 'item_name',
      label: 'Item Name'
    },
    {
      key: 'batch',
      label: 'Batch'
    },
    {
      key: 'total_pack',
      label: 'Qty Kemasan',
      component: (data) => data.total_pack ?? '-'
    },
    {
      key: 'total_qty',
      label: 'Base Qty UOM'
    },
    {
      key: 'location',
      label: 'Location',
      component: (data) => data.location ?? '-'
    },
    {
      key: 'item_problem',
      label: 'Problem',
      component: (data) =>
        data.item_problem ? transformKey(data.item_problem) : '-'
    },
    {
      key: 'bu',
      label: 'BU',
      required: true,
      component: (data) => (
        <Form.Control
          type="text"
          value={data.bu || ''}
          onChange={(e) => handleChangeInput(data.slot_item_id, e.target.value)}
        ></Form.Control>
      )
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (data) => (
        <div
          className="d-flex flex-row gap-2 justify-content-end"
          key={data.slot_item_id}
        >
          <Button
            variant="secondary"
            size="sm"
            className="text-uppercase"
            onClick={() => handleDelete(data)}
          >
            <Trash2 size={16} color="white" />
          </Button>
        </div>
      )
    }
  ]

  return (
    <div>
      <Toaster />
      <Container fluid className="px-4 mb-5">
        <Row>
          <Col md={12} className="mt-3 mb-4">
            <div className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-column justify-content-center">
                <h6 className="header-title-style" style={{ fontSize: '18px' }}>
                  Create Material Issue
                </h6>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="danger"
                  style={{ height: '35px' }}
                  onClick={() => handlePressBack()}
                >
                  Cancel
                </Button>
                <Button
                  className={handleButtonSubmit()}
                  style={{ height: '35px' }}
                  disabled={handleButtonSubmit() === 'btn-secondary'}
                  onClick={handleModal}
                >
                  Create Ticket
                </Button>
              </div>
            </div>
          </Col>
        </Row>
        <Card style={cardStyle} className="mb-4">
          <Row>
            <Col md={12}>
              <h5
                className="mb-3 text-secondary fw-bold"
                style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}
              >
                Items ({data.items.length})
              </h5>
            </Col>
            <Col md={12}>
              <Table
                hover
                responsive
                className="shadow-sm bg-white"
                style={{
                  fontSize: '0.9rem',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}
              >
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    {columns.map((column) => (
                      <th
                        className={`${column.className ?? ''} align-middle p-2`}
                        key={column.key}
                        style={tableHeaderStyle}
                      >
                        <span>
                          {column.label}{' '}
                          {column.required && (
                            <span style={{ color: 'red' }}>*</span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="p-4 text-center text-muted"
                      >
                        No data
                      </td>
                    </tr>
                  )}
                  {data.items.length > 0 &&
                    data.items.map((item) => (
                      <tr
                        key={item.slot_item_id}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className={`p-2 align-middle text-dark ${
                              column.className ?? ''
                            }`}
                          >
                            {column.component && column.component(item)}
                            {!column.component && item[column.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card>
        <div className="mb-5">
          <h5
            className="mb-3 text-secondary fw-bold"
            style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}
          >
            Attachments <sup style={{ color: 'red' }}>*</sup>
          </h5>
          <Row>
            <Col md={12} className="mb-3">
              <div
                style={{
                  border: `2px dashed ${isDragging ? '#f17e29' : '#dee2e6'}`,
                  borderRadius: '12px',
                  backgroundColor: isDragging ? '#fff8f0' : '#f8f9fa',
                  height: '100px',
                  transition: 'all 0.2s ease'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className="d-flex align-items-center justify-content-center"
              >
                <div className="d-flex flex-row justify-content-center align-items-center gap-3">
                  <input
                    type="file"
                    id="upload-file-attachment"
                    className="d-none"
                    ref={uploadFileAttachment}
                    onChange={(e) => handleChangeUploadAttachment(e)}
                    accept="application/pdf"
                  />
                  <Button
                    onClick={() => uploadFileAttachment.current?.click()}
                    className="text-uppercase fw-bold btn-primary-orange btn-sm"
                  >
                    Browse Files
                  </Button>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    or drop PDF files here
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {data.attachments.length > 0 && (
            <Row className="g-3">
              {data.attachments.map((attachment, index) => (
                <Col key={`attachment-${index}`} md={4}>
                  <Card className="h-100 border-0 shadow-sm attachment-card">
                    <Card.Body className="d-flex align-items-center p-3">
                      <div
                        className="bg-light p-3 rounded me-3 d-flex align-items-center justify-content-center"
                        style={{ width: '48px', height: '48px' }}
                      >
                        <FileText className="text-secondary fs-4" size={24} />
                      </div>
                      <div className="flex-grow-1 overflow-hidden">
                        <div
                          className="text-truncate fw-medium"
                          title={attachment?.name}
                          style={{ fontSize: '0.9rem' }}
                        >
                          {attachment?.name || 'File'}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: '0.75rem' }}
                        >
                          {(attachment.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <Button
                        variant="link"
                        className="p-0 text-danger ms-2"
                        onClick={() =>
                          handleRemoveUploadAttachment(Number(index))
                        }
                      >
                        <Trash2 size={16} />
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
        <ModalSeries
          show={showModal}
          handleModal={handleModal}
          submitTicket={handleSubmitTicket}
          changeInput={handleChangeDataInput}
          data={data}
          loading={submitLoading}
        />
      </Container>
    </div>
  )
}

export default CreateMaterialIssuePage
